<template>
	<a
		:href
		class="hotel-list-item-offer"
	>
		<div class="hotel-list-item-offer__container">
			<p class="hotel-list-item-offer__stay-details">
				<span>{{ duration }}&nbsp;Tage, </span>
				<span>{{ `${boardType}${transport === '' ? '' : ', '}` }}</span>
				<span v-if="transport !== ''">{{ transport }}</span>
			</p>
			<div class="hotel-list-item-offer__price">
				<GroupedPriceInfo
					:currency="offer.CurrencyCode"
					:total-price="totalPrice"
					:subtext="priceTypeWording"
					:discount="hasDiscount ? discount : undefined"
				>
					<template #tooltip>
						{{ tooltipText }}
					</template>
				</GroupedPriceInfo>
			</div>
		</div>
	</a>
</template>

<script setup lang="ts">
import type { OfferType } from '@/interfaces/hotel-list-types/hotel-list-types';
import { computed, toRef } from 'vue';
import type { DiscountHotelPriceOffer } from '@/interfaces/neusta-discount-price';
import { boardTypes } from '@/js/data';
import { NBSP } from '@global-js/constants';
import { getPriceTypeWording, getTotalPrice } from '@/js/utils/priceUtils';
import { useDiscount } from '../../composables/discount';
import GroupedPriceInfo from '@lmt-rpb/Molecules/GroupedPriceInfo/GroupedPriceInfo.vue';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';

type Props = {
	offer: OfferType;
	discount?: DiscountHotelPriceOffer | undefined;
	href?: string;
};

const props = defineProps<Props>();
const { isSmallMobile } = storeToRefs(useBreakpointStore());

const isPackageOffer = computed(() => props.offer.TravelType === 'Package');
const duration = computed(() => {
	if (isPackageOffer.value) {
		return props.offer.BetweenDeparturesDuration ?? props.offer.OvernightStays;
	}

	return props.offer.OvernightStays;
});

const boardType = computed(() => {
	const board = boardTypes.find((board) => board.value === props.offer.Board);
	if (!board) {
		return '';
	}
	if (isSmallMobile.value) {
		return board?.label.split(' ').join(NBSP);
	}
	return board?.label;
});
const transport = computed(() => (isPackageOffer.value ? `Inklusive${NBSP}Flug` : ''));

const totalPrice = computed((): number => getTotalPrice(props.offer.Pricing));

const { tooltipText, hasDiscount } = useDiscount(
	toRef(() => props.discount),
	totalPrice.value
);

const priceTypeWording = computed((): string => {
	const price = props.offer.Pricing.Single;

	return getPriceTypeWording({
		type: props.offer.Pricing?.Type,
		perPersonPrice: price.Converted ?? price?.Amount,
		currency: props.offer.CurrencyCode,
	});
});
</script>

<style lang="scss" scoped>
.hotel-list-item-offer {
	&:hover,
	&:focus,
	&:visited,
	&:active {
		text-decoration: none;
	}

	&__container {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		align-items: start;
		margin-top: 0.4rem;
		width: 100%;
		height: 100%;
	}

	&__stay-details {
		font-weight: $font-weight-semibold;
		font-size: $font-small-2;
		color: $color-text-label;
		line-height: 1.6rem;
		margin: 0;
	}

	&__price {
		font-size: $font-small-2;
		place-self: end end;
		color: $color-text-label;

		:deep(.rpb_price) {
			line-height: 2.578rem;
			font-size: $font-medium-1;
			color: $color-extra;
		}

		:deep(.rpb_price > small) {
			line-height: 1.406rem;
			font-size: $font-small-2;
			color: $color-text-label;
		}
	}
}

// a bit smaller than $breakpoint-small, it's the width of the tablet component in the design
@container hotel_item_list (min-width: 72.5rem) {
	.hotel-list-item-offer {
		&__container {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			text-align: right;
			margin-top: 0;
		}

		&__stay-details {
			display: block;
			font-size: $font-small-2;
			line-height: 2rem;
			align-self: flex-end;
		}

		&__price {
			font-size: $font-small-1;
			justify-self: end;
			padding-left: 2.2rem;

			:deep(.rpb_price) {
				line-height: 3.268rem;
				font-size: $font-large-4;
			}

			:deep(.rpb_price > small) {
				line-height: 1.875rem;
				font-size: $font-small-1;
			}
		}
	}
}

// $breakpoint-medium
@container hotel_item_list (min-width: 90rem) {
	.hotel-list-item-offer {
		&__stay-details {
			text-align: right;
			color: $color-text;
			padding-left: 2.2rem;
			margin-top: 3.5rem;
		}

		&__price {
			:deep(.rpb_price) {
				line-height: 3.268rem;
			}

			:deep(.rpb_price > small) {
				line-height: 2.179rem;
			}
		}
	}
}
</style>
